import type {
  ITopArticlesBlockAnalyticsService,
  BlockPlaceRepositoryConfig,
} from '@jtnews/shared/seedwork/frontend/domain';

import type { ILazyBlockUseCase, LazyBlockUseCaseConfig } from '../lazy-block-usecase';
import { LazyBlockUseCase } from '../lazy-block-usecase';

import { TopArticlesBlockUseCaseError } from './top-articles-block-usecase.error';
import type { IGetTopArticlesBlock } from './top-articles-block.api';
import type { TopArticlesNotLazyBlockVM, TopArticleVM } from './top-articles-block.vm';
import type { ITopArticlesLazyBlockPresenter } from './top-articles-lazy-block.presenter';

type CommentClickHandlerParams = {
  event: Event;
  articleId: number;
};

export const TOP_ARTICLES_BLOCK_USE_CASE_KEY = 'top_articles_block_use_case_key';

export interface ITopArticlesBlockUseCase extends ILazyBlockUseCase {
  processArticleClick(articleId: number): void;
  processCommentClick(event: CommentClickHandlerParams): void;
  processBlockShown(items: TopArticleVM[]): void;
}

export interface ITopArticlesBlockStore {
  updateTopArticlesBlock: (block: TopArticlesNotLazyBlockVM) => void;
}

export type TopArticlesBlockUseCaseConfig<TPresenterInputData> =
  LazyBlockUseCaseConfig & {
    regionId: number;
    deps: {
      analyticsService: ITopArticlesBlockAnalyticsService;
      getBlock: IGetTopArticlesBlock<TPresenterInputData>;
      presenter: ITopArticlesLazyBlockPresenter<TPresenterInputData>;
      store: ITopArticlesBlockStore;
    };
  };

export class TopArticlesBlockUseCase<TPresenterInputData>
  extends LazyBlockUseCase
  implements ITopArticlesBlockUseCase
{
  private readonly _analyticsService: ITopArticlesBlockAnalyticsService;
  private readonly _getBlock: IGetTopArticlesBlock<TPresenterInputData>;
  private readonly _presenter: ITopArticlesLazyBlockPresenter<TPresenterInputData>;
  private readonly _regionId: number;
  private readonly _store: ITopArticlesBlockStore;
  private readonly _maxTryGetBlock = 3;

  constructor(config: TopArticlesBlockUseCaseConfig<TPresenterInputData>) {
    super(config);

    const {
      regionId,
      deps: { getBlock, presenter, store, analyticsService },
    } = config;

    this._analyticsService = analyticsService;
    this._getBlock = getBlock;
    this._presenter = presenter;
    this._regionId = regionId;
    this._store = store;
  }

  async getBlock(): Promise<void> {
    try {
      const { data } = await this._getBlock.get({
        regionId: this._regionId,
        retry: { maxTry: this._maxTryGetBlock },
      });
      const block = this._presenter.present({
        data,
        params: null,
      });

      this._store.updateTopArticlesBlock(block);
    } catch (err) {
      this.logger.capture({
        error: TopArticlesBlockUseCaseError.of(
          'Не удалось получить блок TopArticles',
          err as Error,
        ),
      });
    }
  }

  public setBlockPlace(blockPlaceConfig: BlockPlaceRepositoryConfig) {
    this._analyticsService.setBlockPlace(blockPlaceConfig);
  }

  processArticleClick(articleId: number) {
    this._analyticsService.sendEventArticleClick(articleId);
  }

  processBlockShown(items: TopArticleVM[]) {
    this._analyticsService.sendEventBlockShown(items);
  }

  processCommentClick(event: CommentClickHandlerParams) {
    this._analyticsService.sendEventCommentClick(event);
  }
}
